import { ScramjetClient } from "@client/index";
import { unrewriteLinkHeader } from "./xmlhttprequest";
import { String, Object_setPrototypeOf } from "@/shared/snapshot";
import { carrier, carrierNative } from "@client/carrier";

/**
 * Capture the page's intended `init.mode` / `init.credentials` and forward
 * them to `rewriteUrl` so they get stamped onto the proxy URL as `sj$mode` /
 * `sj$cred`. The service-side handler reads those back when computing
 * Sec-Fetch-Mode / Sec-Fetch-Storage-Access, since `event.request.mode` and
 * `event.request.credentials` from the SW are derived against the rewritten
 * same-origin URL and don't reflect the page's actual intent.
 */
function rewriteUrlOptionsForFetch(init: RequestInit | undefined) {
	return {
		// `fetch()` and `new Request()` both default mode to "cors" per spec.
		mode: init?.mode ?? "cors",
		credentials: init?.credentials === "include" ? "include" : undefined,
	};
}

export default function (client: ScramjetClient, self: Self) {
	client.Proxy("fetch", {
		apply(ctx) {
			if (!client.box.instanceof(ctx.args[0], "Request")) {
				const url = String(ctx.args[0]);
				ctx.args[0] = client.rewriteUrl(
					url,
					rewriteUrlOptionsForFetch(ctx.args[1] as RequestInit | undefined)
				);
			}

			// A frame outside the service worker (see carrier.ts): the request is made from its
			// carrier, and what comes back belongs to this frame again.
			const via = carrier(client);
			const viaFetch = via && carrierNative(via, "fetch");
			if (!viaFetch) return;
			const { Promise, Response } = self as any;
			const pending = viaFetch.call(via, ...ctx.args);
			ctx.return(
				new Promise((resolve, reject) => {
					pending.then((response: Response) => {
						Object_setPrototypeOf(response, Response.prototype);
						resolve(response);
					}, reject);
				})
			);
		},
	});

	client.Proxy("Request", {
		construct(ctx) {
			if (client.box.instanceof(ctx.args[0], "Request")) return;
			const url = String(ctx.args[0]);
			ctx.args[0] = client.rewriteUrl(
				url,
				rewriteUrlOptionsForFetch(ctx.args[1] as RequestInit | undefined)
			);
		},
	});

	client.Trap(["Request.prototype.url", "Response.prototype.url"], {
		get(ctx) {
			return client.unrewriteUrl(ctx.get() as string);
		},
	});

	// TODO: this needs to be only for response objects created from a fetch
	client.Trap("Response.prototype.headers", {
		get(ctx) {
			const headers = ctx.get() as Headers;
			const newHeaders = new Headers();

			for (const [key, value] of headers.entries()) {
				if (key.toLowerCase() === "link") {
					newHeaders.append(key, unrewriteLinkHeader(value, client.context));
				} else {
					newHeaders.append(key, value);
				}
			}

			return newHeaders;
		},
	});
}
